import './styles.scss';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import Hashids from 'hashids';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { connect } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Select from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { toast as Toast } from 'react-toastify';
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  Modal,
  UncontrolledAccordion,
} from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import CigarImporter from '@/components/cigars/importer';
import CigarMerger from '@/components/cigars/merger';
import Breadcrumb from '@/components/common/breadcrumb.component';
import MediaCropper from '@/components/cropper';
import Dropzone from '@/components/dropzone';
import ModalDialog from '@/components/modal-dialog';
import ToastContent from '@/components/toasts/content';
import VitolaEditor, { formattedVitolaName } from '@/components/vitolas/editor';
import { Constants } from '@/constants';
import { getCigarById as getCigarByIdAction } from '@/redux/actions/cigar.actions';
import {
  addCigarBarcode,
  type CigarBarcode,
  getBinders,
  getBrands,
  getCigarBarcodes,
  getFillers,
  getManufacturers,
  getOrigins,
  getWrappers,
  postCigar,
  putCigar,
  updateVitolas,
} from '@/utils/actions/cigar';
import { uploadToS3 } from '@/utils/imageUtils';

const hashids = new Hashids('', 12);

const strengths = [
  {
    value: 1,
    label: 'Mellow',
  },
  {
    value: 2,
    label: 'Mellow-Medium',
  },
  {
    value: 3,
    label: 'Medium',
  },
  {
    value: 4,
    label: 'Medium-Full',
  },
  {
    value: 5,
    label: 'Full',
  },
];

interface Option {
  value: string;
  label: string;
}

const CigarEditor = ({ CigarManager, getCigarById }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [hasId, setHasId] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [isPending, setIsPending] = useState(true);

  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [description, setDescription] = useState('');
  const [reviewSummary, setReviewSummary] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [selectedFullName, setSelectedFullName] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedWrapper, setSelectedWrapper] = useState(null);
  const [selectedBinder, setSelectedBinder] = useState(null);
  const [selectedFiller, setSelectedFiller] = useState(null);
  const [selectedStrength, setSelectedStrength] = useState(null);
  const [selectedVitolas, setSelectedVitolas] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [importVitolasOnly, setImportVitolasOnly] = useState(false);
  const [showAddVitolaModal, setShowAddVitolaModal] = useState(false);
  const [barcodes, setBarcodes] = useState<CigarBarcode[]>([]);
  const [showAddBarcodeModal, setShowAddBarcodeModal] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeError, setBarcodeError] = useState('');
  const [loadingBarcodes, setLoadingBarcodes] = useState(false);
  const [barcodeLoadError, setBarcodeLoadError] = useState(false);
  const [savingBarcode, setSavingBarcode] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [selectedImageHolder, setSelectedImageHolder] = useState<string | null>(
    null
  );
  const [selectedImageFile, setSelectedImageFile] = useState<string | null>(
    null
  );
  const [showMediaCropper, setShowMediaCropper] = useState(false);
  const [generating, setGenerating] = useState<string | false>(false);

  const dropzones = {
    cigarImageFull: useRef(null),
    cigarImageBand: useRef(null),
    cigarImageFoot: useRef(null),
    cigarImageDisplay: useRef(null),
    // vitolaImageFull: useRef(null),
  };

  const [cigarImages, setCigarImages] = useState({
    cigarImageFull: null,
    cigarImageBand: null,
    cigarImageFoot: null,
    cigarImageDisplay: null,
    // vitolaImageFull: null,
  });

  useEffect(() => {
    if (searchParams?.has('import')) {
      setShowImportModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    if (hasData && !hasId) {
      // INFO It's a draft - prevent the user from accidentally leaving the page
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasId, hasData]);

  useEffect(() => {
    if (
      !location.state?.cigar ||
      (params.id &&
        params.id !== location.state?.cigar.hash_id &&
        params.id !== 'new')
    ) {
      getCigarById(hashids.decode(params.id));
    }
    setHasId(params.id !== 'new');
  }, [params.id]);

  useEffect(() => {
    let cancelled = false;

    if (!params.id || params.id === 'new') {
      setBarcodes([]);
      setLoadingBarcodes(false);
      setBarcodeLoadError(false);
      return undefined;
    }

    const cigarId = Number(hashids.decode(params.id)[0]);
    if (!Number.isInteger(cigarId)) {
      setBarcodes([]);
      setLoadingBarcodes(false);
      setBarcodeLoadError(true);
      return undefined;
    }

    setLoadingBarcodes(true);
    setBarcodeLoadError(false);
    getCigarBarcodes(cigarId)
      .then((loadedBarcodes) => {
        if (!cancelled) {
          setBarcodes(loadedBarcodes);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(error);
          setBarcodeLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBarcodes(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [params.id]);

  const getBadgeTitle = useCallback(() => {
    if (hasId) {
      return isPending ? 'Pending' : 'Active';
    }
    if (hasData) {
      return 'Draft';
    }
    return null;
  }, [hasId, hasData, isPending]);

  const getBadgeColor = useCallback(() => {
    if (hasId && !isPending) {
      return 'success';
    }
    return 'secondary';
  }, [hasId, hasData, isPending]);

  const loadBrands = useCallback(
    async (inputValue: string): Promise<Option[]> => {
      const brands: string[] = await getBrands(inputValue);
      console.log('Received brands:', brands);
      return brands.map((brand) => ({
        value: brand.id,
        label: brand.name,
      }));
    },
    []
  );

  const loadManufacturers = useCallback((inputValue, callback) => {
    getManufacturers(inputValue).then((manufacturers) => {
      callback(
        manufacturers.map((value) => ({
          value,
          label: value,
        }))
      );
    });
  }, []);

  const loadOrigins = useCallback((inputValue, callback) => {
    getOrigins(inputValue).then((origins) => {
      callback(
        origins.map((value) => ({
          value,
          label: value,
        }))
      );
    });
  }, []);

  const loadWrappers = useCallback((inputValue, callback) => {
    getWrappers(inputValue).then((wrappers) => {
      callback(
        wrappers.map((value) => ({
          value,
          label: value,
        }))
      );
    });
  }, []);

  const loadBinders = useCallback((inputValue, callback) => {
    getBinders(inputValue).then((binders) => {
      callback(
        binders.map((value) => ({
          value,
          label: value,
        }))
      );
    });
  }, []);

  const loadFillers = useCallback((inputValue, callback) => {
    getFillers(inputValue).then((fillers) => {
      callback(
        fillers.map((value) => ({
          value,
          label: value,
        }))
      );
    });
  }, []);

  const populateForm = (cigar) => {
    let wrappers = '';
    if (cigar.wrapper) {
      if (typeof cigar.wrapper === 'string') {
        cigar.wrapper = cigar.wrapper.split(', ');
      }
      wrappers = cigar.wrapper.map((wrapper) => ({
        value: wrapper,
        label: wrapper,
      }));
    }
    let binders = '';
    if (cigar.binder) {
      if (typeof cigar.binder === 'string') {
        cigar.binder = cigar.binder.split(', ');
      }
      binders = cigar.binder.map((binder) => ({
        value: binder,
        label: binder,
      }));
    }
    let fillers = '';
    if (cigar.filler) {
      if (typeof cigar.filler === 'string') {
        cigar.filler = cigar.filler.split(', ');
      }
      fillers = cigar.filler.map((filler) => ({
        value: filler,
        label: filler,
      }));
    }

    setIsPending(cigar.pending);

    setSelectedBrand(
      cigar.brand
        ? {
            value: cigar.brand_id || cigar.brand,
            label: cigar.brand,
          }
        : null
    );
    setSelectedName(cigar.name);
    setSelectedFullName(cigar.full_name);

    setDescription(cigar.description || '');
    setReviewSummary(cigar.review_summary || '');
    setFaqs(cigar.faqs ? JSON.parse(cigar.faqs) : []);

    setSelectedManufacturer(
      cigar.manufacturer
        ? {
            value: cigar.manufacturer,
            label: cigar.manufacturer,
          }
        : null
    );
    setSelectedOrigin(cigar.origin);
    setSelectedOrigin(
      cigar.origin
        ? {
            value: cigar.origin,
            label: cigar.origin,
          }
        : null
    );
    setSelectedWrapper(wrappers);
    setSelectedBinder(binders);
    setSelectedFiller(fillers);
    setSelectedStrength(
      cigar.strength
        ? {
            value: cigar.strength,
            label: strengths.map((s) => s.label)[cigar.strength - 1],
          }
        : null
    );
    setSelectedVitolas(cigar.vitolas || []);
    setSelectedTags(
      cigar.tags
        ? cigar.tags.map((cigarTag) => ({
            value: cigarTag.id,
            label: cigarTag.title,
          }))
        : []
    );

    if (cigar.images) {
      const images = {
        cigarImageFull: cigarImages.cigarImageFull,
        cigarImageBand: cigarImages.cigarImageBand,
        cigarImageFoot: cigarImages.cigarImageFoot,
        cigarImageDisplay: cigarImages.cigarImageDisplay,
      };
      for (let i = 0; i < cigar.images.length; i++) {
        const image = cigar.images[i];
        if (image.approved) {
          if (image.image_type === 0) {
            images.cigarImageFull = image.image_url;
          } else if (image.image_type === 1) {
            images.cigarImageBand = image.image_url;
          } else if (image.image_type === 5) {
            images.cigarImageFoot = image.image_url;
          } else if (image.image_type === 6) {
            images.cigarImageDisplay = image.image_url;
          }
        }
      }
      // console.log('Updated images:', JSON.stringify(images));
      setCigarImages(images);
    }
    setHasData(true);
  };

  useEffect(() => {
    if (location.state?.cigar) {
      const { cigar } = location.state;
      populateForm(cigar);
    }
  }, [location.state?.cigar]);

  const updateCigarMedia = (cigarId: any) => {
    // FIXME Use temp values first? Overwrite existing media if they exist
    const {
      cigarImageFull,
      cigarImageBand,
      cigarImageFoot,
      cigarImageDisplay,
    } = cigarImages;
    if (
      cigarImageFull ||
      cigarImageBand ||
      cigarImageFoot ||
      cigarImageDisplay
    ) {
      console.log('Updating cigar media...');
      console.log({
        full_image_url: cigarImageFull,
        band_image_url: cigarImageBand,
        foot_image_url: cigarImageFoot,
        display_image_url: cigarImageDisplay,
      });
      axios
        .put(`${Constants.apiPath}/cigars/${cigarId}/media`, {
          full_image_url: cigarImageFull,
          band_image_url: cigarImageBand,
          foot_image_url: cigarImageFoot,
          display_image_url: cigarImageDisplay,
        })
        .then(() => {
          // TODO Anything?
        })
        .catch((err) => console.error(err));
    } else {
      console.log('No media to add to cigar...');
    }
  };

  const generateDescription = () => {
    if (params.id !== 'new') {
      setGenerating('description');
      const cigarId = hashids.decode(params.id)[0];

      axios
        .get(`${Constants.aiPath}/admin/cigars/${cigarId}/description`, {})
        .then((response) => {
          setDescription(response.data.description);
        })
        .catch((err) => {
          Toast.error(
            <ToastContent
              message="Unable to generate cigar description. Please contact an admin."
              title="Generation Unsuccessful"
            />,
            {
              position: 'top-right',
            }
          );
          console.error(err);
        })
        .finally(() => setGenerating(false));
    }
  };

  const generateReviewSummary = () => {
    if (params.id !== 'new') {
      setGenerating('review_summary');
      const cigarId = hashids.decode(params.id)[0];

      axios
        .get(`${Constants.aiPath}/admin/cigars/${cigarId}/sessions/summary`, {})
        .then((response) => {
          if (!response.data.summary && response.data.message) {
            Toast.warning(
              <ToastContent
                message={response.data.message}
                title="Generation Unsuccessful"
              />,
              {
                position: 'top-right',
              }
            );
          }
          setReviewSummary(response.data.summary);
        })
        .catch((err) => {
          Toast.error(
            <ToastContent
              message="Unable to generate cigar review summary. Please contact an admin."
              title="Generation Unsuccessful"
            />,
            {
              position: 'top-right',
            }
          );
          console.error(err);
        })
        .finally(() => setGenerating(false));
    }
  };

  const generateFaqs = () => {
    if (params.id !== 'new') {
      setGenerating('faqs');
      const cigarId = hashids.decode(params.id)[0];

      axios
        .get(`${Constants.aiPath}/admin/cigars/${cigarId}/faqs`, {})
        .then((response) => {
          if (!response.data.faqs && response.data.message) {
            Toast.warning(
              <ToastContent
                message={response.data.message}
                title="Generation Unsuccessful"
              />,
              {
                position: 'top-right',
              }
            );
          }
          setFaqs(response.data.faqs);
        })
        .catch((err) => {
          Toast.error(
            <ToastContent
              message="Unable to generate cigar FAQs. Please contact an admin."
              title="Generation Unsuccessful"
            />,
            {
              position: 'top-right',
            }
          );
          console.error(err);
        })
        .finally(() => setGenerating(false));
    }
  };

  const saveCigar = () => {
    // TODO Validate fields and sanitize inputs
    const wrappers = [];
    if (selectedWrapper?.length) {
      for (let i = 0; i < selectedWrapper.length; i++) {
        wrappers.push(selectedWrapper[i].value);
      }
    }

    const binders = [];
    if (selectedBinder?.length) {
      for (let i = 0; i < selectedBinder.length; i++) {
        binders.push(selectedBinder[i].value);
      }
    }

    const fillers = [];
    if (selectedFiller?.length) {
      for (let i = 0; i < selectedFiller.length; i++) {
        fillers.push(selectedFiller[i].value);
      }
    }

    const tags = [];
    if (selectedTags?.length) {
      for (let i = 0; i < selectedTags.length; i++) {
        tags.push(selectedTags[i].label);
      }
    }

    const brandIdValue = selectedBrand?.value;
    const brandId =
      brandIdValue != null && brandIdValue !== '' ? Number(brandIdValue) : NaN;
    const cigar = {
      brand: selectedBrand.label,
      brand_id: Number.isInteger(brandId) ? brandId : undefined,
      name: selectedName,
      description,
      review_summary: reviewSummary,
      faqs: faqs.length ? JSON.stringify(faqs) : null,
      affiliate: selectedManufacturer?.value,
      origin: selectedOrigin?.value,
      wrapper: wrappers.join(', '),
      filler: fillers.join(', '),
      binder: binders.join(', '),
      strength: selectedStrength?.value,
      tags,
      vitolas: selectedVitolas,
      pending: false,
      custom: false,
      cuban: selectedOrigin?.value === 'Cuba', // TODO Do we even really need this in the db?
    };

    // Prevent empty strings
    Object.entries(cigar).forEach(([key, value]) => {
      if (value === '') {
        cigar[key] = null;
      }
    });

    console.log('Submitting cigar:', cigar);

    if (params.id !== 'new') {
      const selectedId = hashids.decode(params.id)[0];
      cigar.id = selectedId;
      putCigar(cigar)
        .then((updatedCigar) => {
          Toast.success(
            <ToastContent
              message="Cigar updated successfully"
              title={updatedCigar.full_name}
            />,
            {
              position: 'top-right',
            }
          );
          setIsPending(false);
          // TODO Anything else?
        })
        .catch((err) => {
          console.error(err);
          ModalDialog.show({
            title: 'Error',
            type: 'error',
            message:
              'Unable to update the cigar details. Please contact an admin.',
            buttons: [
              {
                label: 'Dismiss',
                onClick: () => {
                  ModalDialog.close();
                },
              },
            ],
          });
        });
      updateCigarMedia(selectedId);
      if (selectedVitolas && selectedVitolas.length > 0) {
        updateVitolas(selectedId, selectedVitolas);
      }
    } else {
      postCigar(cigar)
        .then((updatedCigar) => {
          Toast.success(
            <ToastContent
              message="Cigar added successfully!"
              title={updatedCigar.full_name}
            />,
            {
              position: 'top-right',
            }
          );
          updateCigarMedia(updatedCigar.id);
          if (selectedVitolas && selectedVitolas.length > 0) {
            updateVitolas(updatedCigar.id, selectedVitolas);
          }
          setHasId(true);
          setHasData(true);
          setIsPending(false);
          setSelectedFullName(cigar.full_name);
          navigate(`/cigars/editor/${hashids.encode(updatedCigar.id)}`, {
            replace: true,
          });
        })
        .catch((err) => {
          console.error(err);
          ModalDialog.show({
            title: 'Error',
            type: 'error',
            message:
              'Unable to add the cigar details. Please contact an admin.',
            buttons: [
              {
                label: 'Dismiss',
                onClick: () => {
                  ModalDialog.close();
                },
              },
            ],
          });
        });
    }
  };

  const onFileDrop = (imageHolder) => (file) => {
    console.log('Received file for holder:', imageHolder);
    if (file) {
      console.log(file);
      console.log('Loading file...');
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        console.log('Loaded file.');
        const dataUrl = event.target.result;
        console.log(dataUrl);
        setSelectedImageHolder(imageHolder);
        setSelectedImageFile(dataUrl);
        setShowMediaCropper(true);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const uploadMedia = (blob, extension, activeDropzoneId) => {
    setUploadingMedia(true);
    const uuid = uuidv4();
    const cigarHash =
      params.id && params.id !== 'new' ? params.id : new Date().getTime();
    const filename = `${cigarHash}-${activeDropzoneId}.${extension}`;
    uploadToS3(blob, `cigars/${activeDropzoneId}`, filename, uuid)
      .then((res) => {
        console.log('Uploaded to media server!');
        console.log(res.data);
        setUploadingMedia(false);
        // FIXME No need to set to cigarImages here because it will replace the
        //  dropzone image - instead stage it on the cigar somehow?
        setCigarImages((prev) => ({
          ...prev,
          [activeDropzoneId]: res.data.media_url,
        }));
      })
      .catch((err) => {
        setUploadingMedia(false);
        // ErrorLogger.captureException(err);
        console.error(err);
      });
  };

  const renderMediaCropper = () => {
    const activeDropzoneId = selectedImageHolder;
    return (
      <MediaCropper
        aspectRatio={activeDropzoneId === 'cigarImageDisplay' ? 1 : undefined}
        src={selectedImageFile}
        open={showMediaCropper}
        toggle={() => setShowMediaCropper(false)}
        onClose={() => setShowMediaCropper(false)}
        onSave={(croppedCanvas) => {
          console.log('Saving cropped image...');
          croppedCanvas.toBlob((blob) => {
            console.log('Canvas blob');
            console.log(blob);
            console.log(activeDropzoneId);
            const fileReader = new FileReader();
            fileReader.onload = (event) => {
              const dataUrl = event.target.result;
              console.log(dataUrl);
              setCigarImages((prev) => ({
                ...prev,
                [activeDropzoneId]: dataUrl,
              }));
            };
            fileReader.readAsDataURL(blob);

            const base64 = croppedCanvas.toDataURL('image/jpeg');
            uploadMedia(
              blob,
              base64.split(';')[0].split('/')[1],
              activeDropzoneId
            );

            setSelectedImageHolder(null);
            setSelectedImageFile(null);
            setShowMediaCropper(false);
          });
        }}
      />
    );
  };

  const renderAddVitolaModal = () => (
    <VitolaEditor
      open={showAddVitolaModal}
      onClose={() => setShowAddVitolaModal(false)}
      onSave={(vitola) => {
        console.log('Saving vitola:', vitola);
        setSelectedVitolas([...selectedVitolas, vitola]);
        // FIXME If the cigar has already been saved, save the vitola now?
        //  Otherwise, wait to add them during the cigar save action (current logic)?
        setShowAddVitolaModal(false);
      }}
    />
  );

  const closeAddBarcodeModal = () => {
    setShowAddBarcodeModal(false);
    setBarcodeInput('');
    setBarcodeError('');
  };

  const addBarcode = async () => {
    const barcode = barcodeInput.trim();
    if (!barcode) {
      setBarcodeError('Barcode is required.');
      return;
    }
    if (!/^\d+$/.test(barcode)) {
      setBarcodeError('Barcode must contain digits only.');
      return;
    }

    setBarcodeError('');
    setSavingBarcode(true);
    try {
      const cigarId = Number(hashids.decode(params.id)[0]);
      const result = await addCigarBarcode(cigarId, barcode);
      setBarcodes((currentBarcodes) => {
        const existingBarcode = currentBarcodes.find(
          (item) => item.barcode === barcode
        );
        if (existingBarcode) {
          if (!result.created) {
            return currentBarcodes;
          }
          return currentBarcodes.map((item) =>
            item.barcode === barcode
              ? { ...item, productCount: item.productCount + 1 }
              : item
          );
        }
        return [...currentBarcodes, { barcode, productCount: 1 }].sort((a, b) =>
          a.barcode.localeCompare(b.barcode)
        );
      });
      closeAddBarcodeModal();
    } catch (error: any) {
      console.error(error);
      setBarcodeError(
        error.response?.data?.error || 'Unable to add this barcode.'
      );
    } finally {
      setSavingBarcode(false);
    }
  };

  const renderAddBarcodeModal = () => (
    <Modal
      isOpen={showAddBarcodeModal}
      toggle={closeAddBarcodeModal}
      onClosed={closeAddBarcodeModal}
    >
      <div className="modal-header">
        <h5 className="modal-title">Add Barcode</h5>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          addBarcode();
        }}
      >
        <div className="modal-body md">
          <label htmlFor="cigar-barcode" className="form-label">
            Barcode
          </label>
          <input
            id="cigar-barcode"
            className={`form-control${barcodeError ? ' is-invalid' : ''}`}
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            value={barcodeInput}
            onChange={(event) => {
              setBarcodeInput(event.target.value);
              if (barcodeError) {
                setBarcodeError('');
              }
            }}
          />
          {barcodeError && (
            <div className="invalid-feedback">{barcodeError}</div>
          )}
        </div>
        <div className="modal-footer">
          <Button
            type="button"
            color="light"
            onClick={closeAddBarcodeModal}
            disabled={savingBarcode}
          >
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={savingBarcode}>
            Add Barcode
          </Button>
        </div>
      </form>
    </Modal>
  );

  const renderBarcodes = () => {
    if (loadingBarcodes) {
      return <div>Loading barcodes...</div>;
    }
    if (barcodeLoadError) {
      return <div>Unable to load barcodes</div>;
    }
    if (barcodes.length === 0) {
      return <div>No linked barcodes</div>;
    }

    return (
      <div style={{ marginTop: 5 }}>
        {barcodes.map(({ barcode, productCount }, index) => (
          <div
            key={barcode}
            style={{
              paddingTop: 8,
              paddingBottom: 8,
              borderTop: index > 0 ? '1px solid #eeeeee' : undefined,
            }}
          >
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {barcode}
            </div>
            <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
              {productCount} {productCount === 1 ? 'product' : 'products'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Breadcrumb
        title={selectedFullName || 'Add New Cigar'}
        label="Editor"
        parent="Cigars"
        badge={getBadgeTitle()}
        badgeColor={getBadgeColor()}
        onBadgeClick={() => {
          alert('Coming soon!');
          // TODO This is going to allow them to toggle the active state
        }}
      />

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }} />
        {!selectedFullName && (
          <button
            className="btn btn-secondary mr-2"
            onClick={() => {
              setImportVitolasOnly(false);
              setShowImportModal(true);
            }}
            style={
              isMobile
                ? { padding: '6px 14px', marginRight: 12 }
                : { marginRight: 12 }
            }
          >
            {isMobile ? <i className="icon icon-import" /> : 'Import'}
          </button>
        )}
        <button
          className="btn btn-primary btn-sm"
          onClick={saveCigar}
          style={isMobile ? { padding: '6px 14px' } : {}}
        >
          {isMobile ? <i className="icon icon-plus" /> : 'Save'}
        </button>
      </div>

      <div
        className="cigar-editor row"
        style={{ maxWidth: 1200, margin: '24px auto' }}
      >
        <div className="col-md-8">
          <Card style={{ padding: 10 }}>
            <div>
              <span style={{ fontWeight: 700 }}>Images</span>
              {isMobile && (
                <span
                  style={{
                    color: 'rgb(211, 169, 102)',
                    cursor: 'pointer',
                    float: 'right',
                  }}
                  onClick={() => alert('Coming soon')}
                >
                  {'Edit'}
                </span>
              )}
            </div>
            {isMobile ? (
              <div>Coming soon</div>
            ) : (
              <div className="row" style={{ margin: '10px 0' }}>
                <div className="col-md-3">
                  <Dropzone
                    hideIcon
                    id="cigarImageFull"
                    ref={dropzones.cigarImageFull}
                    files={
                      cigarImages.cigarImageFull
                        ? [
                            {
                              preview: cigarImages.cigarImageFull,
                              name: cigarImages.cigarImageFull.substring(
                                cigarImages.cigarImageFull.lastIndexOf('/') + 1
                              ),
                              size: 0,
                            },
                          ]
                        : undefined
                    }
                    dropText="Drop band (on cigar) here"
                    style={{ height: 150, width: 150, padding: 0 }}
                    imagePreviewStyle={{
                      height: 146,
                      width: 146,
                      objectFit: 'contain',
                    }}
                    onDrop={onFileDrop('cigarImageFull')}
                  />
                </div>
                <div className="col-md-3">
                  <Dropzone
                    hideIcon
                    id="cigarImageBand"
                    ref={dropzones.cigarImageBand}
                    files={
                      cigarImages.cigarImageBand
                        ? [
                            {
                              preview: cigarImages.cigarImageBand,
                              name: cigarImages.cigarImageBand.substring(
                                cigarImages.cigarImageBand.lastIndexOf('/') + 1
                              ),
                              size: 0,
                            },
                          ]
                        : undefined
                    }
                    dropText="Drop band (full) here"
                    style={{ height: 150, width: 150, padding: 0 }}
                    imagePreviewStyle={{
                      height: 146,
                      width: 146,
                      objectFit: 'contain',
                    }}
                    onDrop={onFileDrop('cigarImageBand')}
                  />
                </div>
                <div className="col-md-3">
                  <Dropzone
                    hideIcon
                    id="cigarImageFoot"
                    ref={dropzones.cigarImageFoot}
                    files={
                      cigarImages.cigarImageFoot
                        ? [
                            {
                              preview: cigarImages.cigarImageFoot,
                              name: cigarImages.cigarImageFoot.substring(
                                cigarImages.cigarImageFoot.lastIndexOf('/') + 1
                              ),
                              size: 0,
                            },
                          ]
                        : undefined
                    }
                    dropText="Drop additional band (foot) here"
                    style={{ height: 150, width: 150, padding: 0 }}
                    imagePreviewStyle={{
                      height: 146,
                      width: 146,
                      objectFit: 'contain',
                    }}
                    onDrop={onFileDrop('cigarImageFoot')}
                  />
                </div>
                <div className="col-md-3">
                  <Dropzone
                    hideIcon
                    id="cigarImageDisplay"
                    ref={dropzones.cigarImageDisplay}
                    files={
                      cigarImages.cigarImageDisplay
                        ? [
                            {
                              preview: cigarImages.cigarImageDisplay,
                              name: cigarImages.cigarImageDisplay.substring(
                                cigarImages.cigarImageDisplay.lastIndexOf('/') +
                                  1
                              ),
                              size: 0,
                            },
                          ]
                        : undefined
                    }
                    dropText="Drop display image here"
                    style={{ height: 150, width: 150, padding: 0 }}
                    imagePreviewStyle={{
                      height: 146,
                      width: 146,
                      objectFit: 'contain',
                    }}
                    onDrop={onFileDrop('cigarImageDisplay')}
                  />
                </div>
              </div>
            )}
          </Card>
          <Card style={{ padding: 10 }}>
            <div className="row">
              <div className="col-md-6">
                <span style={{ fontWeight: 700 }}>Brand</span>
                <div
                  style={{
                    marginTop: 10,
                    marginBottom: 10,
                  }}
                >
                  <AsyncCreatableSelect
                    value={selectedBrand}
                    onChange={setSelectedBrand}
                    getOptionLabel={(option) => `${option.label}`}
                    getOptionValue={(option) => `${option}`}
                    isOptionSelected={(option) =>
                      selectedBrand && selectedBrand.value === option.value
                    }
                    loadOptions={loadBrands}
                    defaultOptions={CigarManager.brands.map((value) => ({
                      value,
                      label: value,
                    }))}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <span style={{ fontWeight: 700 }}>Name</span>
                <div
                  style={{
                    marginTop: 10,
                    marginBottom: 10,
                  }}
                >
                  <input
                    id="select-name"
                    className="form-control"
                    type="text"
                    name="cigar-name"
                    value={selectedName}
                    style={{ textTransform: 'capitalize' }}
                    onChange={(e) => setSelectedName(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <span style={{ fontWeight: 700 }}>Manufacturer</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <AsyncCreatableSelect
                value={selectedManufacturer}
                onChange={setSelectedManufacturer}
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option}`}
                isOptionSelected={(option) =>
                  selectedManufacturer &&
                  selectedManufacturer.value === option.value
                }
                loadOptions={loadManufacturers}
                defaultOptions={CigarManager.manufacturers.map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </div>
          </Card>
          <Card style={{ padding: 10 }}>
            <span style={{ fontWeight: 700 }}>Origin</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <AsyncCreatableSelect
                value={selectedOrigin}
                onChange={setSelectedOrigin}
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option}`}
                isOptionSelected={(option) =>
                  selectedOrigin && selectedOrigin.value === option.value
                }
                loadOptions={loadOrigins}
                defaultOptions={CigarManager.origins.map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </div>
            <span style={{ fontWeight: 700 }}>Wrapper</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <AsyncCreatableSelect
                value={selectedWrapper}
                onChange={setSelectedWrapper}
                isMulti
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option}`}
                isOptionSelected={(option) =>
                  selectedWrapper && selectedWrapper.value === option.value
                }
                loadOptions={loadWrappers}
                defaultOptions={CigarManager.wrappers.map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </div>
            <span style={{ fontWeight: 700 }}>Binder</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <AsyncCreatableSelect
                value={selectedBinder}
                onChange={setSelectedBinder}
                isMulti
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option}`}
                isOptionSelected={(option) =>
                  selectedBinder && selectedBinder.value === option.value
                }
                loadOptions={loadBinders}
                defaultOptions={CigarManager.binders.map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </div>
            <span style={{ fontWeight: 700 }}>Filler</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <AsyncCreatableSelect
                value={selectedFiller}
                onChange={setSelectedFiller}
                isMulti
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option}`}
                isOptionSelected={(option) =>
                  selectedFiller && selectedFiller.value === option.value
                }
                loadOptions={loadFillers}
                defaultOptions={CigarManager.fillers.map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </div>
            <span style={{ fontWeight: 700 }}>Strength</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <Select
                value={selectedStrength}
                onChange={setSelectedStrength}
                options={strengths}
                getOptionLabel={(option) => `${option.label}`}
                getOptionValue={(option) => `${option}`}
                isOptionSelected={(option) =>
                  selectedStrength && selectedStrength.value === option.value
                }
              />
            </div>
          </Card>
          <Card style={{ padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, flex: 1 }}>Description</span>
              <Button
                disabled={generating === 'description'}
                onClick={generateDescription}
              >
                <AutoAwesomeIcon style={{ fontSize: 16 }} />{' '}
                {generating === 'description' ? 'Generating...' : 'Generate'}
              </Button>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control"
              rows={6}
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            />
            <span style={{ color: description?.length > 900 ? '#e04055' : '' }}>
              {description?.length || 0} / 900
            </span>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, flex: 1 }}>Review Summary</span>
              <Button
                disabled={generating === 'review_summary'}
                onClick={generateReviewSummary}
              >
                <AutoAwesomeIcon style={{ fontSize: 16 }} />{' '}
                {generating === 'review_summary' ? 'Generating...' : 'Generate'}
              </Button>
            </div>
            <textarea
              value={reviewSummary}
              onChange={(e) => setReviewSummary(e.target.value)}
              className="form-control"
              rows={5}
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            />
            <span
              style={{ color: reviewSummary?.length > 600 ? '#e04055' : '' }}
            >
              {reviewSummary?.length || 0} / 600
            </span>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, flex: 1 }}>FAQ</span>
              <Button disabled={generating === 'faqs'} onClick={generateFaqs}>
                <AutoAwesomeIcon style={{ fontSize: 16 }} />{' '}
                {generating === 'faqs' ? 'Generating...' : 'Generate'}
              </Button>
            </div>
            <UncontrolledAccordion defaultOpen="0" style={{ marginTop: 10 }}>
              {faqs.map((faq, index) => (
                <AccordionItem>
                  <AccordionHeader targetId={`${index}`}>
                    {faq.question}
                  </AccordionHeader>
                  <AccordionBody accordionId={`${index}`}>
                    Question:
                    <input
                      value={faq.question}
                      className="form-control"
                      style={{
                        marginTop: 10,
                        marginBottom: 10,
                      }}
                    />
                    Answer:
                    <input
                      value={faq.answer}
                      className="form-control"
                      style={{
                        marginTop: 10,
                        marginBottom: 10,
                      }}
                    />
                  </AccordionBody>
                </AccordionItem>
              ))}
            </UncontrolledAccordion>
          </Card>
        </div>
        <div className="col-md-4">
          <Card style={{ padding: 10 }}>
            <span style={{ fontWeight: 700 }}>Tags</span>
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <AsyncCreatableSelect
                value={selectedTags}
                onChange={setSelectedTags}
                // options={state.tags}
                isMulti
                // getOptionLabel={option =>
                //     `${option}`
                // }
                // getOptionValue={option => `${option}`}
                isOptionSelected={(option) =>
                  selectedTags && selectedTags === option
                }
              />
            </div>
          </Card>

          <Card style={{ padding: 10 }}>
            <div>
              <span style={{ fontWeight: 700 }}>Vitolas</span>
              <Button
                color="light"
                style={{
                  float: 'right',
                  marginLeft: 12,
                }}
                onClick={() => setShowAddVitolaModal(true)}
              >
                {'Add'}
              </Button>
              <Button
                color="light"
                style={{
                  float: 'right',
                }}
                onClick={() => {
                  if (location.state?.cigar) {
                    const { cigar } = location.state;
                    navigate(
                      `/cigars/vitolas/manager?cigar_id=${cigar.hash_id || hashids.encode(cigar.id)}&filter=${cigar.full_name}`
                    );
                  } else {
                    // FIXME Build the cigar and save it automatically, then redirect to vitola manager
                    alert('Please save the cigar first.');
                  }
                }}
              >
                {'Manage'}
              </Button>
            </div>
            {selectedVitolas && selectedVitolas.length > 0 ? (
              selectedVitolas
                .sort((a, b) =>
                  (a.formatted_name || formattedVitolaName(a)).localeCompare(
                    b.formatted_name || formattedVitolaName(b)
                  )
                )
                .map((vitola, index) => (
                  <>
                    <div
                      style={{
                        paddingTop: 5,
                        paddingBottom: 5,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {vitola.formatted_name || formattedVitolaName(vitola)}
                    </div>
                    {vitola.image_url && (
                      <img
                        src={vitola.image_url}
                        alt={vitola.formatted_name}
                        style={{ width: '100%' }}
                      />
                    )}
                  </>
                ))
            ) : (
              <div>No linked vitolas</div>
            )}
            <Button
              color="light"
              style={{
                float: 'right',
                width: '100%',
                marginTop: 10,
              }}
              onClick={() => {
                setImportVitolasOnly(true);
                setShowImportModal(true);
              }}
            >
              {'Sync from URL'}
            </Button>
          </Card>

          <Card style={{ padding: 10 }}>
            <div>
              <span style={{ fontWeight: 700 }}>Barcodes</span>
              <Button
                color="light"
                style={{ float: 'right' }}
                onClick={() => {
                  if (params.id === 'new') {
                    alert('Please save the cigar first.');
                    return;
                  }
                  setShowAddBarcodeModal(true);
                }}
              >
                Add
              </Button>
            </div>
            {renderBarcodes()}
          </Card>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }} />
        {selectedFullName && (
          <>
            <button
              id="merge"
              style={{ marginRight: 10 }}
              type="button"
              className="btn btn-light btn-sm"
              onClick={() => setShowMergeModal(true)}
            >
              {'Merge'}
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => {
                ModalDialog.show({
                  type: 'warning',
                  title: 'Delete Cigar?',
                  message:
                    'This will delete the cigar and all vitolas and cannot be undone. Note: If users have this cigar associated with a smoke session or review, this will return an error. If that happens, you will need to merge the cigar instead.',
                  buttons: [
                    {
                      label: 'No, Cancel',
                      onClick: () => {
                        ModalDialog.close();
                      },
                    },
                    {
                      label: 'Yes, Delete',
                      color: 'danger',
                      onClick: () => {
                        // deleteCigar();
                        alert('Coming back soon!');
                      },
                    },
                  ],
                });
              }}
              style={
                isMobile
                  ? { padding: '6px 14px', marginRight: 10 }
                  : { marginRight: 10 }
              }
            >
              {isMobile ? <i className="icon icon-trash" /> : 'Delete'}
            </button>
          </>
        )}
        <button
          className="btn btn-primary btn-sm"
          onClick={saveCigar}
          style={isMobile ? { padding: '6px 14px' } : {}}
        >
          {isMobile ? <i className="icon icon-plus" /> : 'Save'}
        </button>
      </div>
      <CigarImporter
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        importVitolasOnly={importVitolasOnly}
        onRecievedCigar={(cigar) => {
          populateForm(cigar);
        }}
        onReceivedVitolas={(vitolas) => {
          if (Array.isArray(vitolas) && vitolas.length > 0) {
            // FIXME It really should merge them with existing ones, this would replace them
            setSelectedVitolas(vitolas);
          }
        }}
      />
      <CigarMerger
        open={showMergeModal}
        // sourceCigarId={params.id !== 'new' ? hashids.decode(params.id)[0] : undefined}
        sourceCigar={location.state?.cigar}
        onClose={() => setShowMergeModal(false)}
        onMerged={(cigar) => {
          populateForm(cigar);
          navigate(`/cigars/editor/${cigar.hash_id}`);
        }}
      />
      {renderMediaCropper()}
      {renderAddVitolaModal()}
      {renderAddBarcodeModal()}
    </>
  );
};

const mapStateToProps = ({ CigarManager }) => ({ CigarManager });

export default connect(mapStateToProps, {
  getCigarById: getCigarByIdAction,
})(CigarEditor);
